import React, { useContext, useState } from "react";
import { View, Text, StyleSheet, Pressable } from "react-native";
import { useNavigation } from "@react-navigation/native";
import UserContext from "../../../context/UserContext";
import { useTheme } from "../../../context/ThemeContext";
import { useMobile } from "../../../hooks/useMobile";
import routes from "../../../navigation/routes";
import Logger from "../../../common/utils/Logger";
import { getAuthService } from "@ntconnect/framework/pbx";
import { FONT_BODY } from "../voxcraft/_shared";

function getInitials(name) {
  const parts = String(name || "").trim().split(/[\s.@_-]+/).filter(Boolean);
  if (!parts.length) return "·";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Signed-in account pill for the marketing nav — replaces the Sign in / Get
 * started buttons when authenticated. Avatar + name, with a tap-menu for
 * Dashboard + Sign out.
 */
function NtMarketingAccountPill() {
  const context = useContext(UserContext);
  const { theme } = useTheme();
  const nav = useNavigation();
  const isMobile = useMobile();
  const [open, setOpen] = useState(false);

  const user = context?.user;
  const name = user?.displayName || user?.username || user?.email || "Account";
  const initials = getInitials(name);

  const handleSignOut = async () => {
    setOpen(false);
    try {
      await getAuthService().logout();
    } catch (err) {
      Logger("Marketing sign-out failed (continuing local)", err);
    }
    context.updateUser(null);
  };

  const menuItem = (label, onPress, danger) => (
    <Pressable
      onPress={onPress}
      style={({ pressed }) => [styles.menuItem, pressed && { backgroundColor: theme.surface3 }]}
    >
      <Text style={[styles.menuText, { color: danger ? theme.red : theme.text }]}>{label}</Text>
    </Pressable>
  );

  return (
    <View style={styles.wrap}>
      <Pressable
        onPress={() => setOpen((v) => !v)}
        style={({ pressed }) => [
          styles.pill,
          { backgroundColor: theme.surface2, borderColor: theme.borderSoft },
          pressed && { opacity: 0.85 },
        ]}
      >
        <View style={[styles.avatar, { backgroundColor: theme.brand }]}>
          <Text style={styles.avatarText}>{initials}</Text>
        </View>
        {!isMobile ? (
          <Text style={[styles.name, { color: theme.text }]} numberOfLines={1}>
            {name}
          </Text>
        ) : null}
        <Text style={[styles.chev, { color: theme.textDim }]}>{open ? "▲" : "▼"}</Text>
      </Pressable>

      {open ? (
        <View style={[styles.menu, { backgroundColor: theme.surface, borderColor: theme.borderSoft }]}>
          {menuItem("Dashboard", () => {
            setOpen(false);
            nav.navigate(routes.dashboard);
          })}
          {menuItem("Sign out", handleSignOut, true)}
        </View>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { position: "relative" },
  pill: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingLeft: 4,
    paddingRight: 10,
    paddingVertical: 4,
    borderRadius: 999,
    borderWidth: 1,
    maxWidth: 220,
  },
  avatar: {
    width: 26,
    height: 26,
    borderRadius: 13,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarText: { color: "#fff", fontSize: 11, fontWeight: "700", letterSpacing: 0.4 },
  name: { fontFamily: FONT_BODY, fontSize: 13, fontWeight: "600", flexShrink: 1 },
  chev: { fontSize: 8, marginLeft: 2 },
  menu: {
    position: "absolute",
    top: 44,
    right: 0,
    minWidth: 160,
    borderRadius: 10,
    borderWidth: 1,
    paddingVertical: 6,
    zIndex: 60,
    ...({
      boxShadow: "0 14px 34px -10px rgba(0,0,0,0.55)",
    }),
  },
  menuItem: { paddingVertical: 9, paddingHorizontal: 14 },
  menuText: { fontFamily: FONT_BODY, fontSize: 13, fontWeight: "600" },
});

export default NtMarketingAccountPill;
